import { LegacyPortalResaleService } from "@spwn-portal/grpc";
import useSWR from "swr";
import { isFeatureEnabled } from "utility/isFeatureEnabled";

import { useConnectClient } from "../../utility/connectWeb";

import type {
  LegacyPortalResaleRound,
  LegacyPortalResaleRoundTicket,
} from "@spwn-portal/grpc";

const GET_RESALE_ROUNDS_KEY = "getResaleRounds";

type ResaleStatus = "accepting" | "beforeAcceptance" | "closed";

/**
 * @public 未使用警告を回避。後ほど利用予定。
 */
export type ResaleRounds = {
  resaleRoundId: string;
  resaleRoundTicketId: string;
  eventFirestoreId: string;
  ticketFirestoreId: string;
  ticketName: string;
  ticketPriceAmount: number;
  purchaseFeeAmount: number;
  listingFeeRate: number;
  listingPayoutFeeAmount: number;
  maxApplicableQuantity: number;
  applicationStartAt: number;
  applicationEndAt: number;
  listingStartAt: number;
  listingEndAt: number;
  lotteryDate: number;
  roundNumber: number;
  resaleApplicationStatus: ResaleStatus;
  resaleListingStatus: ResaleStatus;
};

export const useGetResaleRounds = (eventFirestoreId: string) => {
  const connectClient = useConnectClient(LegacyPortalResaleService);
  const isResaleEnabled = isFeatureEnabled({ featureName: "useReSale" });

  const swr = useSWR(
    eventFirestoreId && isResaleEnabled
      ? [GET_RESALE_ROUNDS_KEY, eventFirestoreId]
      : null,
    async () => {
      try {
        const response = await connectClient.getResaleRounds({
          eventFirestoreId,
        });

        return response.resaleRounds.flatMap((resaleRound) =>
          resaleRound.tickets.flatMap((ticket) => {
            const resaleRounds = convertToResaleRounds(resaleRound, ticket);
            if (!resaleRounds) return [];

            return [resaleRounds];
          })
        );
      } catch {
        /**
         * @todo
         * APIのレスポンスが無いので表示確認用にダミーを返すようにしている。
         * APIが完成したらこの処理を削除する。
         */
        return createDummyRounds();
      }
    }
  );

  return { ...swr, isResaleEnabled };
};

/**
 * @public 未使用警告を回避。後ほど利用予定。
 */
export const convertToResaleRounds = (
  round: LegacyPortalResaleRound,
  ticket: LegacyPortalResaleRoundTicket
): ResaleRounds | null => {
  const applicationStartAt = Number(round.applicationStartAt);
  const applicationEndAt = Number(round.applicationEndAt);
  const listingStartAt = Number(round.listingStartAt);
  const listingEndAt = Number(round.listingEndAt);

  return {
    resaleRoundId: round.resaleRoundId,
    resaleRoundTicketId: ticket.resaleRoundTicketId,
    eventFirestoreId: round.eventFirestoreId,
    ticketFirestoreId: ticket.ticketFirestoreId,
    ticketName: ticket.ticketName,
    ticketPriceAmount: ticket.priceJpy,
    purchaseFeeAmount: ticket.serviceFee,
    listingFeeRate: ticket.listingFeeRate,
    listingPayoutFeeAmount: ticket.payoutFee,
    maxApplicableQuantity: ticket.maxApplicableQuantity,
    applicationStartAt,
    applicationEndAt,
    listingStartAt,
    listingEndAt,
    lotteryDate: Number(round.lotteryDate),
    roundNumber: round.roundNumber,
    resaleApplicationStatus: getResaleStatus(
      applicationStartAt,
      applicationEndAt
    ),
    resaleListingStatus: getResaleStatus(listingStartAt, listingEndAt),
  };
};

const getResaleStatus = (startAt: number, endAt: number): ResaleStatus => {
  const currentTime = Date.now();

  if (currentTime < startAt) {
    return "beforeAcceptance";
  }

  if (currentTime <= endAt) {
    return "accepting";
  }

  return "closed";
};

const createDummyRounds = (): ResaleRounds[] => {
  const currentTime = Date.now();
  const applicationEndAt = currentTime + 24 * 60 * 60 * 1000;
  const listingEndAt = applicationEndAt;

  return [
    {
      resaleRoundId: "dummy-resale-round-1",
      resaleRoundTicketId: "dummy-resale-round-ticket-1-a",
      eventFirestoreId: "evt_ph6UDzCu7eFwcRX7g9DU",
      ticketFirestoreId: "zgum8NFsaRyL14OK6WDI",
      ticketName: "指定席A（ハードコードダミーデータ）",
      ticketPriceAmount: 10_000,
      purchaseFeeAmount: 800,
      listingFeeRate: 0.1,
      listingPayoutFeeAmount: 300,
      maxApplicableQuantity: 2,
      applicationStartAt: currentTime,
      applicationEndAt,
      listingStartAt: currentTime,
      listingEndAt,
      lotteryDate: applicationEndAt,
      roundNumber: 1,
      resaleApplicationStatus: "accepting",
      resaleListingStatus: "accepting",
    },
    {
      resaleRoundId: "dummy-resale-round-1",
      resaleRoundTicketId: "dummy-resale-round-ticket-1-b",
      eventFirestoreId: "evt_ph6UDzCu7eFwcRX7g9DU",
      ticketFirestoreId: "1nipOEx5QcvjyPqVTcyi",
      ticketName: "指定席B（ハードコードダミーデータ）",
      ticketPriceAmount: 15_000,
      purchaseFeeAmount: 800,
      listingFeeRate: 0.1,
      listingPayoutFeeAmount: 300,
      maxApplicableQuantity: 2,
      applicationStartAt: currentTime,
      applicationEndAt,
      listingStartAt: currentTime,
      listingEndAt,
      lotteryDate: applicationEndAt,
      roundNumber: 1,
      resaleApplicationStatus: "accepting",
      resaleListingStatus: "accepting",
    },
    {
      resaleRoundId: "dummy-resale-round-2",
      resaleRoundTicketId: "dummy-resale-round-ticket-2-c",
      eventFirestoreId: "evt_ph6UDzCu7eFwcRX7g9DU",
      ticketFirestoreId: "vjMGUsEOFw0LVxlkUgVe",
      ticketName: "指定席C（ハードコードダミーデータ）",
      ticketPriceAmount: 20_000,
      purchaseFeeAmount: 800,
      listingFeeRate: 0.1,
      listingPayoutFeeAmount: 300,
      maxApplicableQuantity: 2,
      applicationStartAt: currentTime + 24 * 60 * 60 * 1000,
      applicationEndAt: applicationEndAt + 24 * 60 * 60 * 1000,
      listingStartAt: currentTime + 24 * 60 * 60 * 1000,
      listingEndAt: listingEndAt + 24 * 60 * 60 * 1000,
      lotteryDate: applicationEndAt,
      roundNumber: 2,
      resaleApplicationStatus: "beforeAcceptance",
      resaleListingStatus: "beforeAcceptance",
    },
  ];
};
